import { useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { getApiBaseUrl, API_V1_PREFIX } from '../config/constants';
import { useAuth } from '../contexts/AuthContext';

export interface VoiceRoom {
  id: string;
  name: string;
  description: string;
  owner: {
    id: string;
    type: string;
  };
  created_by: string;
  type: number;
  status: number;
  settings: {
    max_groups: number;
    is_public: boolean;
    allow_guest_groups: boolean;
    auto_archive_empty_groups: boolean;
    auto_archive_days: number;
    max_created_groups_per_user: number;
    max_owned_groups_per_user: number;
    default_group_id: string;
    allowed_group_types: string[];
    default_group_settings: {
      max_members: number;
      is_public: boolean;
      allow_guest: boolean;
      audio_quality: number;
    };
  };
  groups_count: number;
  created_at: string;
  updated_at: string;
}

export interface VoiceGroup {
  id: string;
  name: string;
  description: string;
  room_id: string;
  owner: {
    id: string;
    type: string;
  };
  created_by: string;
  type: number;
  status: number;
  settings: {
    max_members: number;
    is_public: boolean;
    allow_guest: boolean;
    require_approval: boolean;
    join_code: string;
    audio_quality: number;
    allowed_roles: string[];
  };
  members: {
    members: VoiceGroupMember[];
  };
  position: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface VoiceGroupMember {
  user_id: string;
  userprofile_id: string;
  role: number;
  status: number;
  is_muted: boolean;
  is_deafened: boolean;
  is_speaking: boolean;
  joined_at: string;
  last_active_at: string;
}

export interface RoomStatusUpdate {
  room_id: string;
  status: number;
  update_type: string;
  timestamp: string;
  updated_by_user_id: string;
  reason: string;
  room: VoiceRoom;
}

export interface VoiceGroupStatusUpdate {
  group_id: string;
  status: number;
  update_type: string;
  timestamp: string;
  updated_by_user_id: string;
  group: VoiceGroup;
  from_room_id: string;
  to_room_id: string;
  member_update: {
    user_id: string;
    userprofile_id: string;
    role: number;
    status: number;
    is_muted: boolean;
    is_deafened: boolean;
    is_speaking: boolean;
    reason: string;
  };
}

export const useVoiceService = () => {
  const toast = useToast();
  const { accessToken } = useAuth();
  
  // Create voice API base URL
  const apiBaseUrl = getApiBaseUrl();
  const voiceApiBaseUrl = `${apiBaseUrl}${API_V1_PREFIX}/voice`;
  
  // Create axios config with auth header
  const getAxiosConfig = useCallback(() => {
    return {
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    };
  }, [accessToken]);

  const createRoom = useCallback(async (data: {
    name: string;
    description: string;
    type: number;
    settings: any;
    default_group_name: string;
  }) => {
    try {
      const response = await axios.post(`${voiceApiBaseUrl}/rooms`, data, getAxiosConfig());
      toast({
        title: 'Room created successfully',
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to create room',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const getRooms = useCallback(async (pagination?: any) => {
    try {
      const params = {
        include_archived: false,
        ...(pagination && { pagination }),
      };
      const config = {
        ...getAxiosConfig(),
        params,
      };
      const response = await axios.get(`${voiceApiBaseUrl}/rooms`, config);
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to load rooms',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const getRoom = useCallback(async (roomId: string) => {
    try {
      const response = await axios.get(`${voiceApiBaseUrl}/rooms/${roomId}`, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to load room',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const updateRoom = useCallback(async (roomId: string, data: {
    name: string;
    description: string;
    settings: any;
  }) => {
    try {
      const response = await axios.put(`${voiceApiBaseUrl}/rooms/${roomId}`, data, getAxiosConfig());
      toast({
        title: 'Room updated successfully',
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to update room',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const deleteRoom = useCallback(async (roomId: string, force = false) => {
    try {
      await axios.delete(`${voiceApiBaseUrl}/rooms/${roomId}`, { ...getAxiosConfig(), data: { force } });
      toast({
        title: 'Room deleted successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to delete room',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const getVoiceGroups = useCallback(async (roomId: string, pagination?: any) => {
    try {
      const params = {
        room_id: roomId,
        include_archived: false,
        ...(pagination && { pagination }),
      };
      const response = await axios.get(`${voiceApiBaseUrl}/groups`, { ...getAxiosConfig(), params });
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to load voice groups',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const getVoiceGroup = useCallback(async (groupId: string) => {
    try {
      const response = await axios.get(`${voiceApiBaseUrl}/groups/${groupId}`, getAxiosConfig());
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to load voice group',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const createVoiceGroup = useCallback(async (data: {
    name: string;
    description: string;
    room_id: string;
    type: number;
    settings: any;
    initial_member_ids?: string[];
    is_private: boolean;
  }) => {
    try {
      const response = await axios.post(`${voiceApiBaseUrl}/groups`, data, getAxiosConfig());
      toast({
        title: 'Voice group created successfully',
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to create voice group',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const joinVoiceGroup = useCallback(async (groupId: string, joinCode?: string) => {
    try {
      const response = await axios.post(`${voiceApiBaseUrl}/groups/${groupId}/join`, {
        voice_group_id: groupId,
        join_code: joinCode || '',
        client_type: 'web',
        client_version: '1.0.0',
        keep_existing_connections: false,
      }, getAxiosConfig());
      toast({
        title: 'Joined voice group successfully',
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error: any) {
      toast({
        title: 'Failed to join voice group',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const leaveVoiceGroup = useCallback(async (groupId: string) => {
    try {
      await axios.post(`${voiceApiBaseUrl}/groups/${groupId}/leave`, {
        voice_group_id: groupId,
      }, getAxiosConfig());
      toast({
        title: 'Left voice group successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to leave voice group',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const muteMember = useCallback(async (
    groupId: string, 
    userIds: string | string[] | 'all', 
    reason: string
  ) => {
    try {
      const params = new URLSearchParams();
      if (reason) params.append('reason', reason);
      
      // Handle different input types
      let endpoint = '';
      let body = {};
      
      if (userIds === 'all') {
        // Mute all members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/mute-all`;
      } else if (Array.isArray(userIds)) {
        // Mute multiple members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/mute-batch`;
        body = { user_ids: userIds };
      } else {
        // Mute single member (backward compatibility)
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/${userIds}/mute`;
      }
      
      await axios.post(
        `${endpoint}${params.toString() ? '?' + params.toString() : ''}`,
        body,
        getAxiosConfig()
      );
      
      const count = userIds === 'all' ? 'All members' : 
                   Array.isArray(userIds) ? `${userIds.length} members` : 
                   'Member';
      toast({
        title: `${count} muted successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to mute members',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const unmuteMember = useCallback(async (
    groupId: string, 
    userIds: string | string[] | 'all'
  ) => {
    try {
      // Handle different input types
      let endpoint = '';
      let body = {};
      
      if (userIds === 'all') {
        // Unmute all members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/unmute-all`;
      } else if (Array.isArray(userIds)) {
        // Unmute multiple members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/unmute-batch`;
        body = { user_ids: userIds };
      } else {
        // Unmute single member (backward compatibility)
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/${userIds}/unmute`;
      }
      
      await axios.post(endpoint, body, getAxiosConfig());
      
      const count = userIds === 'all' ? 'All members' : 
                   Array.isArray(userIds) ? `${userIds.length} members` : 
                   'Member';
      toast({
        title: `${count} unmuted successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to unmute members',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const deafenMember = useCallback(async (
    groupId: string, 
    userIds: string | string[] | 'all', 
    reason: string
  ) => {
    try {
      const params = new URLSearchParams();
      if (reason) params.append('reason', reason);
      
      // Handle different input types
      let endpoint = '';
      let body = {};
      
      if (userIds === 'all') {
        // Deafen all members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/deafen-all`;
      } else if (Array.isArray(userIds)) {
        // Deafen multiple members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/deafen-batch`;
        body = { user_ids: userIds };
      } else {
        // Deafen single member (backward compatibility)
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/${userIds}/deafen`;
      }
      
      await axios.post(
        `${endpoint}${params.toString() ? '?' + params.toString() : ''}`,
        body,
        getAxiosConfig()
      );
      
      const count = userIds === 'all' ? 'All members' : 
                   Array.isArray(userIds) ? `${userIds.length} members` : 
                   'Member';
      toast({
        title: `${count} deafened successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to deafen members',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const undeafenMember = useCallback(async (
    groupId: string, 
    userIds: string | string[] | 'all'
  ) => {
    try {
      // Handle different input types
      let endpoint = '';
      let body = {};
      
      if (userIds === 'all') {
        // Undeafen all members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/undeafen-all`;
      } else if (Array.isArray(userIds)) {
        // Undeafen multiple members
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/undeafen-batch`;
        body = { user_ids: userIds };
      } else {
        // Undeafen single member (backward compatibility)
        endpoint = `${voiceApiBaseUrl}/groups/${groupId}/members/${userIds}/undeafen`;
      }
      
      await axios.post(endpoint, body, getAxiosConfig());
      
      const count = userIds === 'all' ? 'All members' : 
                   Array.isArray(userIds) ? `${userIds.length} members` : 
                   'Member';
      toast({
        title: `${count} undeafened successfully`,
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to undeafen members',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  const transferOwnership = useCallback(async (
    groupId: string, 
    newOwnerUserId: string, 
    reason: string = 'Ownership transfer',
    requireAcceptance: boolean = true
  ) => {
    try {
      await axios.post(`${voiceApiBaseUrl}/groups/transfer-ownership`, {
        group_id: groupId,
        new_owner_entity_id: newOwnerUserId,
        new_owner_entity_type: 'userprofile',
        reason: reason,
        require_acceptance: requireAcceptance,
      }, getAxiosConfig());
      toast({
        title: 'Ownership transfer initiated successfully',
        status: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      toast({
        title: 'Failed to transfer ownership',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  }, [voiceApiBaseUrl, toast, getAxiosConfig]);

  return {
    createRoom,
    getRooms,
    getRoom,
    updateRoom,
    deleteRoom,
    getVoiceGroups,
    getVoiceGroup,
    createVoiceGroup,
    joinVoiceGroup,
    leaveVoiceGroup,
    muteMember,
    unmuteMember,
    deafenMember,
    undeafenMember,
    transferOwnership,
  };
};